// BCM-898 · Goods Receipt Dashboard — documents-pending hook (PA1/PA7/PA12).
//
// Outstanding = configured (A3 fetchDocumentTypes) MINUS received (A15 fetchDocuments).
// A3 is NOT reachable today (D2a — workflow-router only). This hook attempts it and
// degrades cleanly: when A3 is unavailable it still returns the received set and
// flags `configuredUnavailable` so the card can explain the backend dependency
// rather than showing an empty list. Card visibility is gated on status + presence
// upstream; completeness comes from configured∖received (never from `fulfilled` — D11).
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { fetchDocumentTypes, fetchDocuments } from "../api/goodsReceiptApi";

// Small per-CMO cache for A3 (configured is keyed on vendorMosymphonyID, not batch).
const configuredCache = {};

export default function useDocumentsPending({
  vendorMosymphonyID,
  schrockenDeliveryScheduleBatchID,
  category, // one of DOC_CATEGORY.* — filters the configured rows
  enabled = true,
}) {
  const [rows, setRows] = useState([]); // [{ name, received }]
  const [loading, setLoading] = useState(false);
  const [configuredUnavailable, setConfiguredUnavailable] = useState(false);
  const mountedRef = useRef(true);
  const cancelRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) cancelRef.current.cancel("leaving documents-pending card");
    };
  }, []);

  useEffect(() => {
    if (!enabled || !vendorMosymphonyID || !schrockenDeliveryScheduleBatchID) return;
    setLoading(true);
    setConfiguredUnavailable(false);

    if (cancelRef.current) cancelRef.current.cancel("superseded documents-pending fetch");
    const source = axios.CancelToken.source();
    cancelRef.current = source;

    const receivedP = fetchDocuments(
      {
        vendorMosymphonyID,
        schrockenDeliveryScheduleBatchID,
      },
      source.token
    )
      .then((res) => (res.data && res.data.status ? res.data.data || [] : []))
      .catch((err) => {
        if (axios.isCancel(err)) throw err;
        return [];
      });

    const configuredP = configuredCache[vendorMosymphonyID]
      ? Promise.resolve(configuredCache[vendorMosymphonyID])
      : fetchDocumentTypes({ vendorMosymphonyID }, source.token)
          .then((res) => {
            if (res.data && res.data.status) {
              const data = res.data.data || [];
              configuredCache[vendorMosymphonyID] = data;
              return data;
            }
            return null; // reachable but errored
          })
          .catch((err) => {
            if (axios.isCancel(err)) throw err;
            return null; // NOT reachable (D2a)
          });

    Promise.all([configuredP, receivedP])
      .then(([configured, received]) => {
        if (!mountedRef.current) return;
        const receivedNames = (received || []).map((n) =>
          typeof n === "string" ? n : n && n.documentTypeName
        );
        if (!configured) {
          // A3 unavailable — show received docs only + flag the dependency.
          setConfiguredUnavailable(true);
          setRows(
            (receivedNames || []).filter(Boolean).map((name) => ({
              name,
              received: true,
            }))
          );
        } else {
          // Only ACTIVATED document types are configured to come in for this CMO.
          const activeConfigured = configured.filter((d) => !!d.activated);
          const inCategory = category
            ? activeConfigured.filter((d) => d.documentCategoryName === category)
            : activeConfigured;
          setRows(
            inCategory.map((d) => {
              // A3 renames documents over time (newDocumentTypeName). fetchDocuments
              // (A15) reports the CURRENT name, so match/display on that when present —
              // matching on the stale documentTypeName misses renamed docs entirely.
              const displayName = d.newDocumentTypeName || d.documentTypeName;
              return {
                name: displayName,
                received: receivedNames.includes(displayName),
              };
            })
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) return; // superseded/unmounted — a newer fetch owns state
        if (!mountedRef.current) return;
        setLoading(false);
      });
  }, [enabled, vendorMosymphonyID, schrockenDeliveryScheduleBatchID, category]);

  const allReceived = rows.length > 0 && rows.every((r) => r.received);
  return { rows, loading, configuredUnavailable, allReceived };
}
